import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { verweistAufEine } from "@/office/excel/formeln";
import { istDatumsformat, istDatumswert, kategorieDirektGelesen } from "@/office/excel/zahlenformat";
import { filledCells, isText, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

/** Sieht der Zellinhalt wie ein getipptes Datum aus, das Excel als Text liest? */
function siehtNachDatumAus(wert: string): boolean {
  return /^\s*\d{1,2}\s*[.\-/]\s*\d{1,2}\s*[.\-/]\s*\d{2,4}\s*$/.test(wert);
}

export const lektionDatum: Lesson = {
  id: "datum",
  title: "Datumswerte: Zahlen, die wie Termine aussehen",
  summary: "Ein Datum ist in Excel eine fortlaufende Zahl. Wer das weiß, kann mit Terminen rechnen.",
  durationMinutes: 10,
  learningGoals: [
    "Erklären, warum ein Datum in Excel eine Zahl ist",
    "Datumswerte so eingeben, dass Excel sie als solche erkennt",
    "Mit Datumswerten rechnen – Fristen, Laufzeiten, Alter",
  ],
  steps: [
    {
      id: "datum-ist-zahl",
      title: "Der 1. Januar 1900 ist die Nummer 1",
      explanation: [
        "Excel speichert Datumswerte als fortlaufende Zahlen: Der 1. Januar 1900 ist " +
          "die 1, jeder weitere Tag zählt eins hoch. Der 1. Januar 2024 ist damit die " +
          "45292. Was du siehst, macht wieder nur das Zahlenformat daraus. Formatierst " +
          "du eine Datumszelle versehentlich als „Standard“, springt dir diese Zahl " +
          "entgegen – kein Fehler, nur die Kulisse ohne Vorhang.",
        "Diese Bauweise ist der Grund, warum sich mit Datumswerten rechnen lässt. " +
          "=B3-B2 zwischen zwei Datumszellen ergibt die Anzahl der Tage dazwischen. " +
          "=HEUTE()+30 ergibt das Datum in dreißig Tagen. Beides funktioniert nur, wenn " +
          "beide Zellen echte Datumswerte enthalten und nicht bloß so aussehen.",
        "Uhrzeiten sind dieselbe Idee mit Nachkommastellen: Ein halber Tag ist 0,5, also " +
          "12:00 Uhr. Ein Datum mit Uhrzeit ist folglich eine Zahl mit Komma. Deshalb " +
          "ergibt die Differenz zweier Zeitstempel einen Bruchteil eines Tages – wer " +
          "Stunden will, multipliziert mit 24.",
      ],
      keyPoints: [
        "Ein Datum ist eine Zahl, eine Uhrzeit ihr Nachkommateil.",
        "Strg + . (Punkt) trägt das heutige Datum als festen Wert ein.",
        "=HEUTE() ist dagegen ein Wert, der sich jeden Tag mitändert.",
      ],
    },
    {
      id: "datum-eingeben",
      title: "Datumswerte richtig eingeben",
      explanation: [
        "Excel erkennt ein Datum, wenn die Eingabe zum Muster der eingestellten " +
          "Systemsprache passt. In einer deutschen Installation sind das 15.3.2024, " +
          "15.03.24 oder 15. März 2024. Alles andere landet als Text in der Zelle – und " +
          "auch hier gilt die Statusanzeige: Ein erkanntes Datum steht rechtsbündig, " +
          "ein missglücktes linksbündig.",
        "Die klassische Falle ist eine Datei aus dem englischsprachigen Raum. Dort " +
          "steht der Monat vorn: 03/15/2024. Ein deutsches Excel liest daraus entweder " +
          "Text – oder, noch tückischer, ein falsches Datum, wenn beide Zahlen kleiner " +
          "als 13 sind. Aus dem 3. Dezember wird dann klammheimlich der 12. März.",
        "Beim Sortieren zeigt sich der Unterschied sofort: Echte Datumswerte sortieren " +
          "chronologisch, als Text gespeicherte alphabetisch. In einer Textspalte kommt " +
          "der 1.12.2024 deshalb vor dem 2.3.2024 – weil „1“ vor „2“ steht.",
      ],
      keyPoints: [
        "Punkt als Trennzeichen im deutschen Excel: 15.3.2024.",
        "Rechtsbündig heißt erkannt, linksbündig heißt Text.",
        "Als Text gespeicherte Datumswerte sortieren alphabetisch, nicht chronologisch.",
      ],
      task: {
        instruction:
          "Ergänze deine Liste um eine Datumsspalte mit mindestens vier echten " +
          "Datumswerten – erkennbar daran, dass sie rechtsbündig stehen.",
        hint: "Im deutschen Excel: 15.3.2024 eintippen. Steht das Ergebnis linksbündig, hat Excel es als Text gelesen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Werttyp und Zahlenformat einer Zelle",
              "1.4",
              "Prüfe die Ausrichtung deiner Datumsspalte: rechtsbündig heißt echtes Datum.",
              true,
            );
          }

          const zellen = filledCells(mappe);
          if (zellen.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          const alsText = zellen.filter(
            (zelle) => isText(zelle) && siehtNachDatumAus(String(zelle.value)),
          );
          if (alsText.length > 0) {
            const erste = alsText[0];
            return fail(
              `In Zelle ${erste.address} steht „${erste.value}“ als Text, nicht als Datum. ` +
                `${alsText.length > 1 ? `Insgesamt betrifft das ${alsText.length} Zellen.` : ""}` +
                "Sortiert würde diese Spalte alphabetisch statt chronologisch.",
              "Tippe das Datum in der Schreibweise deiner Excel-Sprache neu ein, im deutschen " +
                "Excel also mit Punkten: 15.3.2024. Danach muss es rechtsbündig stehen.",
            );
          }

          const datumswerte = zellen.filter(istDatumswert);
          if (datumswerte.length < 4) {
            return fail(
              `Es sind erst ${datumswerte.length} echte Datumswerte vorhanden, gebraucht werden vier.`,
              "Ergänze deiner Liste eine Datumsspalte. Strg + . trägt das heutige Datum ein.",
            );
          }

          const ersatzweg = !kategorieDirektGelesen(datumswerte[0]);
          return pass(
            `${datumswerte.length} echte Datumswerte gefunden, zum Beispiel ${datumswerte[0].address} ` +
              `mit „${datumswerte[0].text}“. Damit lässt sich rechnen und chronologisch sortieren.` +
              (ersatzweg
                ? " (Das Datumsformat wurde über die Formatzeichenfolge erkannt – die " +
                  "Formatkategorie meldet Excel erst ab ExcelApi 1.12 direkt.)"
                : ""),
          );
        },
      },
    },
    {
      id: "mit-datum-rechnen",
      title: "Mit Datumswerten rechnen",
      explanation: [
        "Sobald echte Datumswerte in den Zellen stehen, wird die Rechnerei einfach. " +
          "Die Differenz zweier Datumszellen ergibt Tage. Ein Datum plus 14 ergibt das " +
          "Datum in zwei Wochen. Beides braucht keine besondere Funktion, nur Minus und " +
          "Plus – Excel weiß ja, dass es Zahlen sind.",
        "Ein Detail überrascht regelmäßig: Das Ergebnis einer Datumsdifferenz erbt oft " +
          "das Datumsformat der Ausgangszellen. Statt „30“ zeigt die Zelle dann den " +
          "30. Januar 1900. Der Wert stimmt, nur das Format ist falsch – Abhilfe " +
          "schafft, die Ergebniszelle auf „Standard“ zu stellen.",
        "Für Fälligkeiten und Fristen sind zwei Funktionen die Arbeitspferde: " +
          "HEUTE() liefert das aktuelle Datum und aktualisiert sich bei jedem Öffnen " +
          "der Datei, JETZT() dasselbe mit Uhrzeit. Beide haben keine Argumente, die " +
          "leeren Klammern gehören trotzdem dazu.",
      ],
      keyPoints: [
        "=B3-B2 zwischen zwei Datumszellen ergibt die Anzahl der Tage.",
        "Zeigt das Ergebnis ein Datum statt einer Zahl: Format auf „Standard“ stellen.",
        "=HEUTE() rechnet sich bei jedem Öffnen neu, Strg + . bleibt fest stehen.",
      ],
      task: {
        instruction:
          "Lege eine Zelle an, die mit deinen Datumswerten rechnet – zum Beispiel die " +
          "Anzahl Tage zwischen zwei Daten oder ein Fälligkeitsdatum aus einem Datum " +
          "plus einer Frist.",
        hint: "=B3-B2 für die Tage dazwischen, oder =B3+14 für zwei Wochen später. Zeigt das Ergebnis ein Datum statt einer Zahl, das Format auf „Standard“ stellen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formel einer Zelle",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob deine Rechnung auf die Datumszellen verweist.",
            );
          }

          const zellen = filledCells(mappe);
          const datumszellen = zellen.filter(istDatumswert);
          if (datumszellen.length === 0) {
            return fail(
              "Es gibt noch keine echten Datumswerte, mit denen sich rechnen ließe.",
              "Löse zuerst die Aufgabe im vorigen Schritt.",
            );
          }

          // Eine Rechnung mit Datumsbezug: eine Formel, die auf mindestens eine
          // der Datumszellen verweist, oder HEUTE()/JETZT() benutzt.
          const datumsAdressen = datumszellen.map((zelle) => zelle.address);
          const rechnungen = zellen.filter((zelle) => {
            if (!zelle.formula.startsWith("=")) return false;
            if (/\b(HEUTE|TODAY|JETZT|NOW)\s*\(/i.test(zelle.formula)) return true;
            return verweistAufEine(zelle.formula, datumsAdressen);
          });

          if (rechnungen.length === 0) {
            return fail(
              "Keine Formel verweist bisher auf eine deiner Datumszellen.",
              `Schreibe zum Beispiel in eine freie Zelle =${datumszellen[datumszellen.length - 1].address}-${datumszellen[0].address} ` +
                "für die Tage dazwischen – oder =HEUTE() minus einem deiner Daten.",
            );
          }

          const erste = rechnungen[0];
          const zeigtDatum = istDatumsformat(erste) && !/\b(HEUTE|TODAY|JETZT|NOW)\s*\(\s*\)\s*$/i.test(erste.formula);
          if (zeigtDatum && /-/.test(erste.formula)) {
            return fail(
              `Die Rechnung in ${erste.address} („${erste.formula}“) stimmt, wird aber als Datum ` +
                `angezeigt: „${erste.text}“. Eine Anzahl Tage ist aber eine Zahl.`,
              "Stell die Ergebniszelle auf das Zahlenformat „Standard“. Der Wert ist bereits richtig – " +
                "die Zelle hat nur das Datumsformat der Ausgangszellen geerbt.",
            );
          }

          return pass(
            `In ${erste.address} rechnet „${erste.formula}“ mit Datumswerten und zeigt „${erste.text}“.`,
          );
        },
      },
    },
  ],
};
